import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCustomer } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';

// ────────────────────────────────────────────────────────────────────────────────

export const useJoinVerificationForm = (
  props: UseJoinVerificationFormProps,
) => {
  const { defaultValues = { code: '' } } = props;
  const { t } = useLocalizationContext();
  const { signUpVerificationResponseData } = useCustomer();

  // ────────────────────────────────────────────────────────────────────

  const codeFieldExactCharsMessage = t('form.validation-length', {
    characters: VALID_CODE_CHARS_LENGTH,
  });

  const validationSchema = useMemo(
    () =>
      yup
        .object({
          code: yup
            .string()
            .length(VALID_CODE_CHARS_LENGTH, codeFieldExactCharsMessage),
        })
        .required(),
    [codeFieldExactCharsMessage],
  );

  // ────────────────────────────────────────────────────────────────────

  const form = useForm<JoinVerificationFormData>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  // ────────────────────────────────────────────────────────────────────

  // handle login mutation errors
  useEffect(() => {
    const validationErrorCase =
      signUpVerificationResponseData?.__typename === 'ValidationError';

    if (validationErrorCase) {
      // eslint-disable-next-line unicorn/no-array-for-each
      signUpVerificationResponseData.fieldErrors.forEach((fieldError) => {
        const fieldName = fieldError.field as keyof JoinVerificationFormData;

        form.setError(fieldName, {
          type: 'validate',
          message: fieldError.message,
        });
      });
    }
  }, [form, signUpVerificationResponseData]);

  return { form };
};

// ─── HELPERS ────────────────────────────────────────────────────────────────────

const emailValidationSchema = yup
  .object({ email: yup.string().email().required() })
  .required();

export const isEmailValid = (email: string) =>
  emailValidationSchema.isValidSync({ email });

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type JoinVerificationFormData = {
  readonly code: string;
};

type UseJoinVerificationFormProps = {
  readonly defaultValues: Partial<JoinVerificationFormData>;
};

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const VALID_CODE_CHARS_LENGTH = 6;
